"use client";

import { useEffect, useRef, useState } from "react";

import clsx from "clsx";

export default function BackgroundVideo({ className, src, poster, priority }) {
  const videoRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
    const isSmallScreen = window.matchMedia?.("(max-width: 991px)")?.matches ?? false;
    const connection = navigator?.connection || navigator?.mozConnection || navigator?.webkitConnection;
    const saveData = Boolean(connection?.saveData);
    const effectiveType = connection?.effectiveType || "";
    const slowConnection = effectiveType.includes("2g");

    setShouldLoad(!(prefersReducedMotion || isSmallScreen || saveData || slowConnection));
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !shouldLoad) {
      return;
    }

    setIsReady(video.readyState >= 2);

    const ensurePlay = () => {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          /* autoplay might be blocked; ignore */
        });
      }
    };

    const handleLoadedData = () => {
      setIsReady(true);
      ensurePlay();
    };

    const handleTimeUpdate = () => {
      if (!video.duration) {
        return;
      }
      const remaining = video.duration - video.currentTime;
      if (remaining <= 0.08) {
        video.currentTime = 0;
        ensurePlay();
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        ensurePlay();
      }
    };

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.loop = false;
    video.load();

    ensurePlay();

    video.addEventListener("loadeddata", handleLoadedData);
    video.addEventListener("timeupdate", handleTimeUpdate);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      video.removeEventListener("loadeddata", handleLoadedData);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [src, shouldLoad]);

  return (
    <video
      ref={videoRef}
      className={clsx(className)}
      src={shouldLoad ? src : undefined}
      poster={poster || undefined}
      muted
      playsInline
      disablePictureInPicture
      data-ready={isReady ? "true" : "false"}
      preload={shouldLoad && priority ? "auto" : "metadata"}
      style={{ objectPosition: "var(--section-background-position-current)" }}
    >
      {shouldLoad && <source src={src} type="video/mp4" />}
    </video>
  );
}
